import { AutocompleteController } from '@components/controllers/AutocompleteController';
import { CheckboxController } from '@components/controllers/CheckboxController';
import { TextFieldController } from '@components/controllers/TextFieldController';
import ModalActions from '@components/modal/ModalActions';
import ModalContent from '@components/modal/ModalContent';
import { useCreateDeviceForm } from '@features/createDevice/hooks/useCreateDeviceForm';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box } from '@mui/material';
import { type FC } from 'react';
import { hasSensors, integrationTypeOptions, typeOptions } from './deviceTypes';
import StandardAutocompleteIdsWithFallback from '@features/standardDesign/components/autocomplete/StandardAutocompleteIds';
import { Controller } from 'react-hook-form';
import { SensorsController } from './SensorsController';
import { NumericTextFieldController } from '@components/controllers/NumericTextFieldController';

interface Props {
  onClose: () => void;
}

export const CreateDeviceForm: FC<Props> = ({ onClose }) => {
  const { _ } = useLingui();
  const { control, watch, onSubmit, formState } = useCreateDeviceForm({ onClose });

  const isIntegration = watch('isIntegration');
  const type = watch('type');

  return (
    <form onSubmit={onSubmit}>
      <ModalContent>
        <Box display="grid" gap={2}>
          <TextFieldController control={control} name="name" label={<Trans>Nombre</Trans>} />

          <CheckboxController
            control={control}
            name="isIntegration"
            label={<Trans>¿Es un dispositivo de integración?</Trans>}
          />

          <AutocompleteController
            control={control}
            name="type"
            options={(isIntegration ? integrationTypeOptions : typeOptions).map(
              (option) => option.label,
            )}
            label={<Trans>Tipo</Trans>}
            slotProps={{
              autocomplete: {
                disableClearable: true,
              },
            }}
          />

          {isIntegration && (
            <Controller
              control={control}
              name="integrationId"
              render={({ field: { ref, ...field }, fieldState }) => (
                <StandardAutocompleteIdsWithFallback
                  {...field}
                  dataType="integrations"
                  value={field.value}
                  onChange={(_, value) => field.onChange(value)}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          )}

          {hasSensors(type) && <SensorsController control={control} />}

          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            <NumericTextFieldController
              control={control}
              name="latitude"
              label={<Trans>Latitud</Trans>}
            />
            <NumericTextFieldController
              control={control}
              name="longitude"
              label={<Trans>Longitud</Trans>}
            />
          </Box>

          <Controller
            control={control}
            name="organizationId"
            render={({ field: { ref, ...field }, fieldState }) => (
              <StandardAutocompleteIdsWithFallback
                {...field}
                dataType="organizations"
                value={field.value}
                onChange={(_, value) => field.onChange(value)}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />

          <Controller
            control={control}
            name="collectionIds"
            render={({ field: { ref, ...field }, fieldState }) => (
              <StandardAutocompleteIdsWithFallback
                {...field}
                dataType="icnCollections"
                value={field.value?.[0] ?? null}
                onChange={(_, value) => field.onChange(value != null ? [value] : [])}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />

          <NumericTextFieldController
            control={control}
            name="shares"
            label={<Trans>Cantidad de acciones</Trans>}
          />
        </Box>
      </ModalContent>
      <ModalActions
        onClose={onClose}
        onSubmit="submit"
        submitLabel={_(msg`Crear`)}
        submitLoading={formState.isSubmitting}
      />
    </form>
  );
};
